/**
 * The stable error-code catalogue — `docs/architecture/BOUNDARIES.md` § 6, restated by
 * `contracts/operations.md` § Common envelope.
 *
 * Exactly eight codes exist. They are stable across every operation, so a client can branch on the
 * code without parsing anything. Two rules from § 6 are structural rather than stylistic:
 *
 *  1. **One Georgian message key per code, and only a key.** The server never sends display text;
 *     it sends `code` plus the key the client resolves in its own Georgian catalogue. That is what
 *     keeps a server string from becoming a user-visible message, and it is why this module maps to
 *     `MessageKey` values and holds no Georgian text at all.
 *  2. **`FORBIDDEN` never discloses existence.** Its response is byte-identical whether or not the
 *     target exists (§ 5.5, FR-VIS-08), which is only possible because the code and its key carry no
 *     target-specific content.
 *
 * The mapping is a bijection: eight codes, eight distinct keys.
 */
import { type MessageKey } from './types';
import { z } from 'zod';

/**
 * The eight codes of `BOUNDARIES.md` § 6, in the order that document lists them.
 *
 * Frozen at runtime: `isErrorCode` reads this array while `errorCodeSchema` and `ERROR_MESSAGE_KEYS`
 * are built from it once at module load. Without the freeze a single `push` would make the guard
 * accept a ninth code that the schema rejects and that has no message key — the bijection § 6
 * requires would be broken from outside this module.
 */
export const ERROR_CODES = Object.freeze([
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'INVALID_ARGUMENT',
  'FAILED_PRECONDITION',
  'CONFLICT',
  'RATE_LIMITED',
  'INTERNAL',
] as const);

export type ErrorCode = (typeof ERROR_CODES)[number];

export const errorCodeSchema = z.enum(ERROR_CODES);

/**
 * Code → the single Georgian message key the client resolves.
 *
 * `Record<ErrorCode, MessageKey>` makes a missing code a compile error; the accompanying test proves
 * the other direction (no extra key, no key used twice).
 */
export const ERROR_MESSAGE_KEYS: Readonly<Record<ErrorCode, MessageKey>> = Object.freeze({
  UNAUTHENTICATED: 'error.unauthenticated',
  FORBIDDEN: 'error.forbidden',
  NOT_FOUND: 'error.notFound',
  INVALID_ARGUMENT: 'error.invalidArgument',
  FAILED_PRECONDITION: 'error.failedPrecondition',
  CONFLICT: 'error.conflict',
  RATE_LIMITED: 'error.rateLimited',
  INTERNAL: 'error.internal',
});

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && (ERROR_CODES as readonly string[]).includes(value);
}

/** Total over `ErrorCode`; the registry above is exhaustive by type. */
export function messageKeyForError(code: ErrorCode): MessageKey {
  return ERROR_MESSAGE_KEYS[code];
}
